import React, { useEffect, useState, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Link } from 'react-router-dom';
import AdminPanel from './AdminPanel';

interface UserStats {
  totalSearches: number;
  facilitiesViewed: number;
  lastActivity: string;
}

interface FacilityStats {
  total_facilities: number;
  total_tanks: number;
  active_tanks: number;
  average_tank_age: number;
  total_leaks: number;
  facilities_with_leaks: number;
  leak_rate: number;
}

const Dashboard: React.FC = () => {
  const { isAuthenticated, user } = useAuth0();
  const [userRole, setUserRole] = useState<string>('general');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [stats] = useState<UserStats>({
    totalSearches: 0,
    facilitiesViewed: 0,
    lastActivity: 'Never'
  });
  const [facilityStats, setFacilityStats] = useState<FacilityStats>({
    total_facilities: 0,
    total_tanks: 0,
    active_tanks: 0,
    average_tank_age: 0,
    total_leaks: 0,
    facilities_with_leaks: 0,
    leak_rate: 0
  });
  const [loadingFacilityStats, setLoadingFacilityStats] = useState<boolean>(true);

  // Non-blocking background data loading
  const loadUserDataBackground = useCallback(async () => {
    if (!user?.email) return;
    
    try {
      // Only update role if we don't have cached data or it's stale (>5 minutes)
      const lastApiCall = localStorage.getItem(`lastApiCall_${user.email}`);
      const now = Date.now();
      const fiveMinutes = 5 * 60 * 1000;
      
      if (lastApiCall && (now - parseInt(lastApiCall)) < fiveMinutes) {
        return; // Skip API call if recent cache exists
      }
      
      // Background API call that doesn't block UI
      setTimeout(async () => {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/user-profile`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sub: user.sub,
            email: user.email,
            name: user.name
          })
        });
        
        if (response.ok) {
          const userProfile = await response.json();
          setUserRole(userProfile.role);
          
          // Update cache
          localStorage.setItem(`userRole_${user.email}`, userProfile.role);
          localStorage.setItem(`lastApiCall_${user.email}`, now.toString());
          
          console.log('Background role update:', userProfile.role);
        }
      }, 100); // Small delay to ensure UI renders first
      
    } catch (error) {
      console.warn('Background role update failed:', error);
    }
  }, [user, setUserRole]);

  // Load facility statistics with caching
  const loadFacilityStatistics = useCallback(async () => {
    // Check cache first
    const cacheKey = 'facility_statistics';
    const cachedData = sessionStorage.getItem(cacheKey);
    const cacheTimestamp = sessionStorage.getItem(`${cacheKey}_timestamp`);
    const now = Date.now();
    const cacheMaxAge = 10 * 60 * 1000; // 10 minutes cache

    // Use cached data if available and not expired
    if (cachedData && cacheTimestamp && (now - parseInt(cacheTimestamp)) < cacheMaxAge) {
      try {
        const parsedData = JSON.parse(cachedData);
        console.log('Using cached facility statistics');
        setFacilityStats(parsedData);
        setLoadingFacilityStats(false);
        return;
      } catch (error) {
        console.warn('Failed to parse cached facility stats, fetching fresh data');
      }
    }

    try {
      setLoadingFacilityStats(true);
      console.log('Loading fresh facility statistics');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/facilities-statistics`);
      
      if (response.ok) {
        const data = await response.json();
        setFacilityStats(data);
        console.log('Facility statistics loaded:', data);
        
        // Cache the statistics
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify(data));
          sessionStorage.setItem(`${cacheKey}_timestamp`, now.toString());
          console.log('Cached facility statistics for future use');
        } catch (cacheError) {
          console.warn('Failed to cache facility statistics:', cacheError);
        }
      } else {
        console.warn('Failed to load facility statistics:', response.status);
      }
    } catch (error) {
      console.error('Error loading facility statistics:', error);
    } finally {
      setLoadingFacilityStats(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && user) {
      // Immediately set to non-loading state for instant render
      setIsLoading(false);
      
      // Check for cached user role first for instant display
      const cachedRole = localStorage.getItem(`userRole_${user.email}`);
      setUserRole(cachedRole || 'general');
      
      // Load user data in background without blocking
      loadUserDataBackground();
      
      // Load facility statistics
      loadFacilityStatistics();
      
      console.log('Dashboard loaded instantly for user:', user.email);
    }
  }, [isAuthenticated, user, loadUserDataBackground, loadFacilityStatistics]);


  if (!isAuthenticated) {
    return (
      <div className="container">
        <p>Please log in to access the dashboard.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="container">
        <div className="loading-container">
          <div>Loading dashboard...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div style={{ marginBottom: '2rem' }}>
        <h2>Dashboard</h2>
        <p>Your role: <strong>{userRole}</strong></p>
      </div>

      <div className="features">
        <div className="feature-card">
          <h3>Your Activity</h3>
          <div style={{ fontSize: '1.1rem', lineHeight: '1.6' }}>
            <p>Total Searches: <strong>{stats.totalSearches}</strong></p>
            <p>Facilities Viewed: <strong>{stats.facilitiesViewed}</strong></p>
            <p>Last Activity: <strong>{stats.lastActivity}</strong></p>
          </div>
        </div>

        <div className="feature-card">
          <h3>Quick Actions</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <Link to="/county-map" className="btn btn-primary">🗺️ USA Risk Map</Link>
            <Link to="/search" className="btn btn-secondary">🔍 Search Facilities</Link>
          </div>
        </div>

        <div className="feature-card">
          <h3>Facility Database Statistics</h3>
          {loadingFacilityStats ? (
            <div style={{ fontSize: '1rem', color: '#666' }}>Loading facility data...</div>
          ) : (
            <div style={{ fontSize: '1.1rem', lineHeight: '1.6' }}>
              <p>Total Facilities: <strong>{facilityStats.total_facilities.toLocaleString()}</strong></p>
              <p>Total Tanks: <strong>{facilityStats.total_tanks.toLocaleString()}</strong></p>
              <p>Active Tanks: <strong>{facilityStats.active_tanks.toLocaleString()}</strong></p>
              <p>Average Tank Age: <strong>{facilityStats.average_tank_age.toFixed(1)} years</strong></p>
              <p>Total Leaks: <strong>{facilityStats.total_leaks.toLocaleString()}</strong></p>
              <p>Leak Rate: <strong>{facilityStats.leak_rate}%</strong></p>
            </div>
          )}
        </div>

        {userRole === 'admin' && (
          <div className="feature-card">
            <h3>Admin Tools</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <button className="btn btn-primary">User Management</button>
              <button className="btn btn-secondary">Data Updates</button>
              <button className="btn btn-secondary">System Health</button>
            </div>
          </div>
        )}

        {userRole === 'broker' && (
          <div className="feature-card">
            <h3>Broker Tools</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <button className="btn btn-primary">Email Network</button>
              <button className="btn btn-secondary">Client Reports</button>
            </div>
          </div>
        )}
      </div>

      {userRole === 'admin' && (
        <div style={{ marginTop: '3rem' }}>
          <AdminPanel />
        </div>
      )}
    </div>
  );
};

export default Dashboard;